import { CheckCircleIcon, WarningCircleIcon } from '@phosphor-icons/react';
import {
  Button,
  EmptyState,
  FileDropzone,
  Modal,
  Switch,
} from '@skupreme/shared/ui';
import { brand } from '@utility/config';
import { CommonUtility } from '@utility/common';
import { ErrorMessageConstant } from '@utility/constants/common';
import ItemService from '@utility/services/item';
import UploadService from '@utility/services/upload';
import { ToastMessage } from '@utility/toast';
import { useEffect, useState } from 'react';

type Outcome = 'idle' | 'done' | 'failed';

const apiMessage = (error: unknown) =>
  (error as { error?: string } | null)?.error ||
  ErrorMessageConstant.defaultAPIError;

/** Imports carton attributes from a CSV in the template's metric or imperial units. */
export function BulkUploadModal({
  open,
  closeModal,
  imperial: pageImperial = false,
}: {
  open: boolean;
  closeModal: () => void;
  imperial?: boolean;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [imperial, setImperial] = useState(pageImperial);
  const [outcome, setOutcome] = useState<Outcome>('idle');
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFiles([]);
    setOutcome('idle');
    setImperial(pageImperial);
  }, [open, pageImperial]);

  const upload = async () => {
    const file = files[0];
    if (!file) return;
    try {
      setUploading(true);
      const { signedUrlPut } =
        await UploadService.getItemsImportAttributesSignedUrl(imperial);
      await UploadService.media(signedUrlPut, file, file.type);
      setOutcome('done');
    } catch (error) {
      setOutcome('failed');
      ToastMessage.error(apiMessage(error));
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      setDownloading(true);
      const template = await ItemService.getAttributesTemplate(imperial);
      if (template?.data) {
        CommonUtility.downloadFile(
          template.data,
          `${Date.now()}-${imperial ? 'imperial' : 'metric'}.csv`,
        );
      }
    } catch (error) {
      ToastMessage.error(apiMessage(error));
    } finally {
      setDownloading(false);
    }
  };

  const footer =
    outcome === 'idle' ? (
      <>
        <Button variant="ghost" onPress={closeModal} isDisabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onPress={upload}
          isDisabled={!files.length}
          isPending={uploading}
        >
          Import
        </Button>
      </>
    ) : outcome === 'failed' ? (
      <>
        <Button variant="ghost" onPress={closeModal}>
          Cancel
        </Button>
        <Button variant="primary" onPress={() => setOutcome('idle')}>
          Retry
        </Button>
      </>
    ) : (
      <Button variant="primary" onPress={closeModal}>
        Got it
      </Button>
    );

  return (
    <Modal
      isOpen={open}
      onOpenChange={isOpen => !isOpen && closeModal()}
      isDismissable={!uploading}
      size="md"
      title="Import carton attributes"
      footer={footer}
    >
      {outcome === 'done' ? (
        <EmptyState
          icon={<CheckCircleIcon size={32} className="sk:text-success" />}
          title="Your data has been imported"
          description={`Your attributes will appear in ${brand.name} shortly.`}
        />
      ) : outcome === 'failed' ? (
        <EmptyState
          icon={<WarningCircleIcon size={32} className="sk:text-danger" />}
          title="We could not import your data"
          description="Check that the file follows the template. If it keeps failing, get in touch with us."
        />
      ) : (
        <div className="sk:flex sk:flex-col sk:gap-4">
          <div className="sk:flex sk:items-center sk:justify-between sk:gap-3">
            <Button
              variant="pill"
              size="sm"
              onPress={downloadTemplate}
              isPending={downloading}
            >
              Download template
            </Button>
            <Switch isSelected={imperial} onChange={setImperial}>
              Imperial
            </Switch>
          </div>
          <FileDropzone
            value={files}
            onChange={setFiles}
            accept={['.csv']}
            multiple={false}
            label="Attributes file"
            hint="One CSV file in the template's columns"
          />
        </div>
      )}
    </Modal>
  );
}
